import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { useEffect, useState } from "react";
import { setup } from "./lib/api";

import AgentAutofix from "./components/AgentAutofix";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Setup from "./pages/Setup";
import Dashboard from "./pages/Dashboard";
import Businesses from "./pages/Businesses";
import BusinessDetail from "./pages/BusinessDetail";
import WebsiteDetail from "./pages/DomainDetail";
import AIStudio from "./pages/AIStudio";
import Affiliates from "./pages/Affiliates";
import Settings from "./pages/Settings";
import CreateWebsite from "./pages/CreateWebsite";
import DLQ from "./pages/DLQ";

/** Guard: redirect to /login if there is no session (the httpOnly API cookie). */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { tenant, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenant) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Show the login page. LEGACY (LEGACY_PASSWORD_LOGIN=on): redirect a first install
 * with no admin to the password /setup page. With Titanium login a fresh install is
 * bootstrapped through OPEN_SIGNUP instead.
 */
function LoginOrSetup() {
  const navigate = useNavigate();
  const { isLoading, config } = useAuth();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    if (!config.legacyPasswordLogin) {
      setChecking(false);
      return;
    }
    setup.status()
      .then((res) => {
        if (res.needsSetup) {
          navigate("/setup", { replace: true });
        } else {
          setChecking(false);
        }
      })
      .catch(() => {
        setChecking(false); // API down — show login anyway
      });
  }, [navigate, isLoading, config.legacyPasswordLogin]);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return <Login />;
}

/** LEGACY first-run password setup; hidden unless LEGACY_PASSWORD_LOGIN=on. */
function LegacySetup() {
  const { isLoading, config } = useAuth();
  if (isLoading) return null;
  return config.legacyPasswordLogin ? <Setup /> : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <>
    <AgentAutofix />
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginOrSetup />} />
      <Route path="/setup" element={<LegacySetup />} />

      {/* Protected — all inside Layout shell */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="create-website" element={<CreateWebsite />} />
        <Route path="businesses" element={<Businesses />} />
        <Route path="businesses/:id" element={<BusinessDetail />} />
        <Route path="websites/:id" element={<WebsiteDetail />} />
        {/* Legacy redirects */}
        <Route path="websites" element={<Navigate to="/businesses" replace />} />
        <Route path="domains" element={<Navigate to="/businesses" replace />} />
        <Route path="domains/:id" element={<Navigate to="/businesses" replace />} />
        <Route path="clusters" element={<Navigate to="/businesses" replace />} />
        <Route path="content" element={<Navigate to="/businesses" replace />} />
        <Route path="studio" element={<AIStudio />} />
        <Route path="analytics" element={<Navigate to="/businesses" replace />} />
        <Route path="affiliates" element={<Affiliates />} />
        <Route path="settings" element={<Settings />} />
        <Route path="dlq" element={<DLQ />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
